import React, { useRef, useEffect, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize } from "lucide-react";
import { formatTime, calculateDrift } from "../utils/videoSyncManager";

// Load YouTube IFrame API
const loadYouTubeAPI = () => {
  return new Promise((resolve) => {
    if (window.YT && window.YT.Player) {
      resolve();
      return;
    }

    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      resolve();
    };
  });
};

function YouTubePlayer({ videoId, isHost, socketManager, partyStatus, parentContainerRef }) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(100);
  const [isMuted, setIsMuted] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [drift, setDrift] = useState(0);
  const heartbeatIntervalRef = useRef(null);
  const timeUpdateIntervalRef = useRef(null);
  const driftRef = useRef(0);

  // Initialize YouTube player
  useEffect(() => {
    let player = null;

    const initPlayer = async () => {
      await loadYouTubeAPI();

      player = new window.YT.Player(containerRef.current, {
        videoId: videoId,
        playerVars: {
          autoplay: 0,
          controls: 0, // Hide YouTube controls, use custom
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
        },
        events: {
          onReady: (event) => {
            playerRef.current = event.target;
            setIsReady(true);
            setDuration(event.target.getDuration());
            console.log("YouTube player ready");
          },
          onStateChange: (event) => {
            const isVideoPlaying = event.data === window.YT.PlayerState.PLAYING;
            setIsPlaying(isVideoPlaying);

            // Host: Emit play/pause events
            if (isHost) {
              if (event.data === window.YT.PlayerState.PLAYING) {
                const currentTime = playerRef.current.getCurrentTime();
                console.log("[WP-DEBUG] YouTube host PLAYING → emitPlay", {
                  currentTime,
                });
                socketManager.emitPlay(currentTime);
              } else if (event.data === window.YT.PlayerState.PAUSED) {
                const currentTime = playerRef.current.getCurrentTime();
                console.log("[WP-DEBUG] YouTube host PAUSED → emitPause", {
                  currentTime,
                });
                socketManager.emitPause(currentTime);
              }
            } else {
              console.log("[WP-DEBUG] YouTube onStateChange (participant, no emit)", {
                state: event.data,
              });
            }
          },
        },
      });
    };

    initPlayer();

    return () => {
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
      }
    };
  }, [videoId, isHost, socketManager]);

  // Update current time continuously
  useEffect(() => {
    if (!isReady) return;

    timeUpdateIntervalRef.current = setInterval(() => {
      if (playerRef.current && playerRef.current.getCurrentTime) {
        setCurrentTime(playerRef.current.getCurrentTime());
      }
    }, 100);

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, [isReady]);

  // Host: Send periodic heartbeat
  useEffect(() => {
    if (!isHost || !isReady) return;

    heartbeatIntervalRef.current = setInterval(() => {
      if (playerRef.current) {
        const currentTime = playerRef.current.getCurrentTime();
        const state = playerRef.current.getPlayerState();
        const isPlaying = state === window.YT.PlayerState.PLAYING;
        socketManager.emitHeartbeat(currentTime, isPlaying);
      }
    }, 5000);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
      }
    };
  }, [isHost, isReady, socketManager]);

  // Participant: Listen to sync events
  useEffect(() => {
    if (isHost || !socketManager || !isReady) return;

    let syncCheckInterval = null;

    const handleSyncState = (data) => {
      if (!playerRef.current) return;

      const { currentTime: targetTime, isPlaying: targetPlaying } = data;
      const currentVideoTime = playerRef.current.getCurrentTime();
      const currentDrift = calculateDrift(currentVideoTime, targetTime);
      setDrift(currentDrift);
      driftRef.current = currentDrift;

      // Sync playback state
      if (targetPlaying && playerRef.current.getPlayerState() !== window.YT.PlayerState.PLAYING) {
        playerRef.current.playVideo();
      } else if (!targetPlaying && playerRef.current.getPlayerState() === window.YT.PlayerState.PLAYING) {
        playerRef.current.pauseVideo();
      }

      // Sync time if drift exceeds threshold
      if (currentDrift > 2) {
        setIsSyncing(true);
        playerRef.current.seekTo(targetTime, true);
        setTimeout(() => setIsSyncing(false), 1000);
      } else if (currentDrift > 0.5 && currentDrift <= 2) {
        // Minor drift - adjust playback speed (YouTube API doesn't support this well)
        // Just do a gentle seek
        if (currentDrift > 1) {
          playerRef.current.seekTo(targetTime, true);
        }
      }
    };

    const handlePlay = (data) => {
      console.log("[WP-DEBUG] YouTube recv play", {
        isReady,
        hasPlayer: !!playerRef.current,
        data,
      });
      if (!playerRef.current) return;
      const { currentTime: targetTime } = data;
      
      setIsSyncing(true);
      playerRef.current.seekTo(targetTime, true);
      playerRef.current.playVideo();
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handlePause = (data) => {
      if (!playerRef.current) return;
      const { currentTime: targetTime } = data;
      
      setIsSyncing(true);
      playerRef.current.seekTo(targetTime, true);
      playerRef.current.pauseVideo();
      setTimeout(() => setIsSyncing(false), 500);
    };

    const handleSeek = (data) => {
      if (!playerRef.current) return;
      const { currentTime: targetTime } = data;
      
      setIsSyncing(true);
      playerRef.current.seekTo(targetTime, true);
      setTimeout(() => setIsSyncing(false), 500);
    };

    // Periodic drift check
    syncCheckInterval = setInterval(() => {
      if (!playerRef.current) return;
      const state = playerRef.current.getPlayerState();
      if (state !== window.YT.PlayerState.PLAYING) return;

      if (driftRef.current > 1) {
        socketManager.requestSync();
      }
    }, 3000);

    socketManager.on("sync-state", handleSyncState);
    socketManager.on("play", handlePlay);
    socketManager.on("pause", handlePause);
    socketManager.on("seek", handleSeek);

    // Request initial sync
    setTimeout(() => socketManager.requestSync(), 500);

    return () => {
      if (syncCheckInterval) clearInterval(syncCheckInterval);
      socketManager.off("sync-state", handleSyncState);
      socketManager.off("play", handlePlay);
      socketManager.off("pause", handlePause);
      socketManager.off("seek", handleSeek);
    };
  }, [isHost, socketManager, isReady]);

  const togglePlayPause = () => {
    if (!playerRef.current) return;

    const state = playerRef.current.getPlayerState();
    if (state === window.YT.PlayerState.PLAYING) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  };

  const handleSeek = (e) => {
    if (!playerRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const seekTime = percent * duration;
    
    playerRef.current.seekTo(seekTime, true);
    
    // Host: Emit seek event
    if (isHost) {
      socketManager.emitSeek(seekTime);
    }
  };

  const toggleMute = () => {
    if (!playerRef.current) return;

    if (playerRef.current.isMuted()) {
      playerRef.current.unMute();
      setIsMuted(false);
    } else {
      playerRef.current.mute();
      setIsMuted(true);
    }
  };

  const handleVolumeChange = (e) => {
    if (!playerRef.current) return;

    const newVolume = parseInt(e.target.value);
    playerRef.current.setVolume(newVolume);
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    // Use parent container if provided (for chat overlay), otherwise use iframe
    const elementToFullscreen = parentContainerRef?.current || containerRef.current?.querySelector("iframe");
    if (!elementToFullscreen) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      elementToFullscreen.requestFullscreen().catch(err => {
        console.error("Fullscreen request failed:", err);
      });
    }
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="relative bg-slate-950 rounded-xl overflow-hidden border border-slate-700 h-full min-h-[400px] flex flex-col">
      {/* YouTube Player Container */}
      <div
        ref={containerRef}
        className="w-full aspect-video bg-black flex-1"
      />

      {/* Loading Overlay */}
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-emerald-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-slate-300 text-sm">Loading YouTube player...</p>
          </div>
        </div>
      )}

      {/* Sync Indicator */}
      {isSyncing && (
        <div className="absolute top-4 left-4 bg-emerald-500/90 text-white px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-2 z-10">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
          Syncing...
        </div>
      )}

      {/* Host Badge */}
      {isHost && (
        <div className="absolute top-4 right-4 bg-violet-500/90 text-white px-3 py-1.5 rounded-full text-xs font-semibold z-10">
          HOST
        </div>
      )}

      {/* Participant Mode Notice */}
      {!isHost && (
        <div className="absolute top-4 right-4 bg-slate-800/90 text-slate-300 px-3 py-1.5 rounded-full text-xs z-10">
          Following host
        </div>
      )}

      {/* Custom Controls */}
      {isReady && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4 z-10">
          {/* Progress Bar */}
          <div
            className="w-full h-1.5 bg-slate-700 rounded-full cursor-pointer mb-3 group"
            onClick={isHost ? handleSeek : undefined}
          >
            <div
              className="h-full bg-red-500 rounded-full relative group-hover:bg-red-400 transition"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition" />
            </div>
          </div>

          {/* Control Buttons */}
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              {/* Play/Pause */}
              <button
                onClick={isHost ? togglePlayPause : undefined}
                disabled={!isHost}
                className={`p-2 rounded-full transition ${
                  isHost
                    ? "hover:bg-white/20 active:scale-95"
                    : "opacity-50 cursor-not-allowed"
                }`}
                title={isHost ? "Play/Pause" : "Only host can control"}
              >
                {isPlaying ? <Pause size={20} /> : <Play size={20} />}
              </button>

              {/* Time */}
              <span className="text-sm font-medium">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>

              {/* Drift Indicator (Participants) */}
              {!isHost && drift > 0.5 && (
                <span className="text-xs text-emerald-400">
                  Drift: {drift.toFixed(1)}s
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              {/* Volume */}
              <div className="flex items-center gap-2 group">
                <button
                  onClick={toggleMute}
                  className="p-2 rounded-full hover:bg-white/20 transition"
                >
                  {isMuted || volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="10"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-1 bg-slate-600 rounded-full appearance-none cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
                />
              </div>

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-full hover:bg-white/20 transition"
              >
                <Maximize size={18} />
              </button>
            </div>
          </div>

          {/* Status Message */}
          {partyStatus === "waiting" && (
            <div className="mt-2 text-center text-sm text-slate-400">
              Waiting for party to start...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default YouTubePlayer;
